import { ChangeDetectionStrategy, Component, type OnInit, inject, OnDestroy, signal, ViewChild, ElementRef, AfterViewInit, computed, effect } from '@angular/core';
import { CommonModule, NgClass, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'primeng/tooltip';
import { PopoverModule } from 'primeng/popover';
import { DatePickerModule } from 'primeng/datepicker';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SelectModule } from 'primeng/select';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { ActivatedRoute } from '@angular/router';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { Subscription } from 'rxjs';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-logs',
  standalone: true,
  imports: [
    CommonModule,
    NgClass,
    DatePipe,
    FormsModule,
    TooltipModule,
    PopoverModule,
    DatePickerModule,
    ButtonModule,
    DialogModule,
    SelectButtonModule,
    SelectModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule
  ],
  templateUrl: './logs.html',
  styleUrl: './logs.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Logs implements OnInit, OnDestroy, AfterViewInit {
  private api = inject(CloudApiService);
  private route = inject(ActivatedRoute);
  private messageService = inject(MessageService);

  private logSub?: Subscription;
  // When true, we auto-scroll as new logs arrive; signal for template reactivity
  autoScroll = signal<boolean>(true);
  private logsContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('logsContainer') set logsContainerSetter(el: ElementRef<HTMLDivElement> | undefined) {
    this.logsContainer = el;
    // Attempt initial scroll once container materializes and logs are present
    if (el && this.logs().length && !this.initialScrollDone) {
      this.scheduleInitialScroll();
    }
  }
  private mutationObserver?: MutationObserver;
  readonly logs = signal<any[]>([]);
  readonly filteredLogs = computed(() => {
    const minLevel = this.selectedLogLevel();
    const allLogs = this.logs();
    if (minLevel === 'all') return allLogs;

    // Get minimum level severity index
    const minIndex = this.levelHierarchy.indexOf(minLevel.toLowerCase());
    if (minIndex === -1) return allLogs;

    // Filter logs that are at or above the minimum severity level
    return allLogs.filter(log => {
      const logLevel = log.level?.toLowerCase();
      if (!logLevel) return false;
      const logIndex = this.levelHierarchy.indexOf(logLevel);
      return logIndex >= minIndex;
    });
  });
  private deviceId: string | null = null;
  private nextCursor: string | null = null;
  private loadingOlder = false;
  private loadingNewer = false;
  private idSet = new Set<string>(); // Tracks timestampString to prevent duplicates
  // Breadcrumbs handled by parent device component
  // Loading state for initial batch (used by @defer placeholder)
  initialLoading = signal<boolean>(true);

  // Helper to get unique key for log deduplication
  private getLogKey(log: any): string | null {
    return log.timestampString || log.timestamp?.getTime().toString() || null;
  }

  private initialScrollDone = false;

  // Time navigation state
  logTimeRange = signal<{ earliest: Date | null; latest: Date | null }>({ earliest: null, latest: null });
  selectedDateTime = signal<Date | null>(null);
  isHistoricalView = signal<boolean>(false); // true when viewing historical logs, false when in live mode
  showDatePicker = signal<boolean>(false);

  // Log level filter - using minimum level (inclusive of higher severity)
  selectedLogLevel = signal<string>('trace');
  logLevelOptions = [
    { label: 'Trace +', value: 'trace' },
    { label: 'Debug +', value: 'debug' },
    { label: 'Info +', value: 'information' },
    { label: 'Warning +', value: 'warning' },
    { label: 'Error +', value: 'error' },
    { label: 'Critical', value: 'critical' }
  ];

  // Level severity hierarchy (lower index = lower severity)
  private readonly levelHierarchy = ['trace', 'debug', 'information', 'info', 'warning', 'warn', 'error', 'critical', 'fatal'];

  private initialLoadComplete = false;

  // Search functionality
  searchQuery = signal<string>('');
  private searchDebounceTimer?: any;

  // Export dialog
  showExportDialog = signal(false);
  exporting = signal(false);
  exportProgress = signal<{ fetched: number } | null>(null);
  exportFromDate: Date | null = null;
  exportToDate: Date | null = null;
  exportLevelFilter = 'trace';

  private deviceName = '';

  constructor() {
    // Watch for filter changes and refetch logs from backend
    effect(() => {
      const level = this.selectedLogLevel();
      // Skip initial load (ngOnInit handles that)
      if (!this.initialLoadComplete || !this.deviceId) return;

      console.log('Log level filter changed to:', level);
      this.refetchLogsWithFilter();
    });

    // Watch for search changes and refetch logs from backend
    effect(() => {
      const search = this.searchQuery();
      // Skip initial load (ngOnInit handles that)
      if (!this.initialLoadComplete || !this.deviceId) return;

      console.log('Search query changed to:', search);
      this.refetchLogsWithFilter();
    });
  }

  onSearchChange(value: string): void {
    // Debounce search input to avoid excessive API calls
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchDebounceTimer = setTimeout(() => {
      this.searchQuery.set(value);
    }, 400);
  }

  clearSearch(): void {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchQuery.set('');
  }

  // Helper method to check if a log matches the current search pattern
  private matchesSearchPattern(log: any): boolean {
    const searchTerm = this.searchQuery().trim().toLowerCase();
    if (!searchTerm) return true; // No search term means all logs match

    const message = (log.message || '').toLowerCase();
    return message.includes(searchTerm);
  }

  // Popover state
  @ViewChild('logPopover') logPopover?: any;
  selectedLog = signal<any>(null);
  hoveredLog = signal<any>(null);
  popoverVisible = signal<boolean>(false);

  onLogMouseEnter(log: any): void {
    this.hoveredLog.set(log);
  }

  onLogMouseLeave(): void {
    this.hoveredLog.set(null);
  }

  onLogClick(event: MouseEvent, log: any): void {
    // If clicking on the same log that's already showing, close it
    if (this.popoverVisible() && this.selectedLog() === log) {
      if (this.logPopover) {
        this.logPopover.hide();
      }
      this.popoverVisible.set(false);
      this.selectedLog.set(null);
      return;
    }

    // If another popover is open, hide it first and wait before showing new one
    if (this.popoverVisible() && this.logPopover) {
      this.logPopover.hide();
      // Wait for the popover to close before opening the new one
      setTimeout(() => {
        this.selectedLog.set(log);
        this.popoverVisible.set(true);
        if (this.logPopover) {
          this.logPopover.show(event);
        }
      }, 50);
      return;
    }

    // Show new popover (no existing popover open)
    this.selectedLog.set(log);
    this.popoverVisible.set(true);
    if (this.logPopover) {
      this.logPopover.show(event);
    }
  }

  onPopoverHide(): void {
    this.popoverVisible.set(false);
    this.selectedLog.set(null);
  }

  getLogClasses(log: any): any {
    return {
      [log.level]: true,
      'hovered': this.hoveredLog() === log,
      'popover-active': this.popoverVisible() && this.selectedLog() === log
    };
  }

  getLogKeys(log: any): string[] {
    if (!log) return [];
    return Object.keys(log).filter(key => !key.startsWith('_'));
  }

  formatLogValue(value: any, key?: string): string {
    if (value === null || value === undefined) return 'null';
    if (key === 'timestamp' && value instanceof Date) {
      // For timestamp, check if we have a high-precision string available
      const log = this.selectedLog();
      if (log?.timestampString) {
        return log.timestampString; // Show full precision string
      }
      return value.toISOString(); // Fallback to ISO string
    }
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  }

  ngOnInit(): void {
    // deviceId now resides on parent route (':deviceId') since this is a nested child
    this.deviceId = this.route.parent?.snapshot.paramMap.get('deviceId') || this.route.snapshot.paramMap.get('deviceId');
    if (!this.deviceId) {
      console.warn('No deviceId in route params');
      return;
    }
    // Fetch latest 100 logs (one-shot)
    // Build breadcrumbs (device + namespace/location chain if available)
    this.api.getDevice(this.deviceId).then(d => {
      if (d?.name) this.deviceName = d.name;
      const level = this.selectedLogLevel();
      const search = this.searchQuery();
      this.api.getDeviceLogs(this.deviceId!, 200, undefined, undefined, undefined, level, search)
        .then((resp) => {
          console.log('Fetched device logs:', resp);
          const initialRaw = Array.isArray(resp) ? resp : (resp && Array.isArray(resp.items) ? resp.items : []);
          this.nextCursor = (resp as any)?.nextCursor || null;
          const normalized = this.normalizeLogs(initialRaw);
          this.logs.set(normalized);
          normalized.forEach(l => { const key = this.getLogKey(l); if (key) this.idSet.add(key); });
          let firstEmission = true;
          // Subscribe to live updates
          this.logSub = this.api.subscribeToDeviceLogs(this.deviceId!).subscribe(allLogs => {
            console.log('Received live log update:', allLogs);
            const rawArray = Array.isArray(allLogs) ? allLogs : [];
            const liveNorm = this.normalizeLogs(rawArray);
            if (firstEmission && liveNorm.length === 0 && normalized.length > 0) {
              firstEmission = false;
              return;
            }
            firstEmission = false;
            // Filter for new logs that match search pattern
            const newOnes = liveNorm.filter(l => {
              const key = this.getLogKey(l);
              return key && !this.idSet.has(key) && this.matchesSearchPattern(l);
            });
            if (newOnes.length === 0) return;
            newOnes.forEach(l => { const key = this.getLogKey(l); if (key) this.idSet.add(key); });
            const highlighted = newOnes.map(l => ({ ...l, _new: true }));
            const wasAtBottom = this.isAtBottom();
            const wasNearBottom = this.isNearBottom();
            this.logs.update(arr => [...arr, ...highlighted]);
            console.log('Appended new logs:', highlighted, { wasAtBottom, wasNearBottom, auto: this.autoScroll() });
            if (this.autoScroll() && (wasAtBottom || wasNearBottom)) {
              // Scroll after DOM paints new line height
              requestAnimationFrame(() => {
                this.scrollToBottom();
                // If minor drift remains (fonts async), attempt one more frame
                if (!this.isAtBottom()) {
                  requestAnimationFrame(() => this.scrollToBottom());
                }
              });
            }
            if (highlighted.length) {
              setTimeout(() => {
                this.logs.update(arr => arr.map(item => item._new ? { ...item, _new: false } : item));
              }, 1800);
            }
          });
          // Mark loading complete and schedule initial scroll now that container will render
          this.initialLoading.set(false);
          this.initialLoadComplete = true; // Allow effect to start watching filter changes
          // If container already rendered (unlikely with @defer), run scroll; else setter will trigger
          if (this.logsContainer && !this.initialScrollDone) this.scheduleInitialScroll();
        })
        .catch(err => {
          this.messageService.add({ severity: 'error', summary: 'Failed to Fetch Logs', detail: err?.error || err?.message || 'An error occurred while fetching device logs' });
          this.initialLoading.set(false);
          this.initialLoadComplete = true;
          if (this.logsContainer && !this.initialScrollDone) this.scheduleInitialScroll();
        });
    }).catch(() => {
      // ignore
    });

  }

  openExportDialog(): void {
    const range = this.logTimeRange();
    this.exportFromDate = range.earliest ?? new Date(Date.now() - 24 * 60 * 60 * 1000);
    this.exportToDate = new Date();
    this.exportLevelFilter = this.selectedLogLevel();
    this.exportProgress.set(null);
    this.showExportDialog.set(true);
  }

  async exportLogs(): Promise<void> {
    if (!this.deviceId) return;
    this.exporting.set(true);
    this.exportProgress.set({ fetched: 0 });

    const from = this.exportFromDate;
    const to = this.exportToDate;
    const level = this.exportLevelFilter;
    const MAX_ENTRIES = 100_000;

    const allLogs: any[] = [];
    const seen = new Set<string>();
    let anchor = from ?? new Date(0);
    let keepFetching = true;

    try {
      while (keepFetching && allLogs.length < MAX_ENTRIES) {
        const resp = await this.api.getDeviceLogs(this.deviceId, 500, undefined, anchor, 'after', level, undefined);
        const items: any[] = resp?.items ?? (Array.isArray(resp) ? resp : []);
        if (items.length === 0) break;

        const normalized = this.normalizeLogs(items);
        for (const log of normalized) {
          const key = log.timestampString ?? log.timestamp?.getTime().toString() ?? Math.random().toString();
          if (seen.has(key)) continue;
          seen.add(key);
          if (to && log.timestamp > to) { keepFetching = false; break; }
          allLogs.push(log);
        }

        this.exportProgress.set({ fetched: allLogs.length });

        if (keepFetching) {
          const last = normalized[normalized.length - 1];
          if (normalized.length < 500 || !last?.timestamp) {
            keepFetching = false;
          } else {
            anchor = last.timestamp;
          }
        }
      }

      const text = this.formatLogsAsText(allLogs, from, to, level);
      this.triggerDownload(text, 'logs.txt');
      this.showExportDialog.set(false);
    } catch (err: any) {
      this.messageService.add({ severity: 'error', summary: 'Export Failed', detail: err?.error || 'Failed to export logs' });
    } finally {
      this.exporting.set(false);
      this.exportProgress.set(null);
    }
  }

  private formatLogsAsText(logs: any[], from: Date | null, to: Date | null, level: string): string {
    const pad = (s: string, len: number) => s.length >= len ? s : s + ' '.repeat(len - s.length);
    const fmtDate = (d: Date | null) => d ? d.toISOString().replace('T', ' ').replace('Z', ' UTC') : '—';
    const fmtTs = (log: any): string => {
      if (log.timestampString) {
        // Convert ISO to readable: 2024-01-15T08:23:15.123Z → 2024-01-15 08:23:15.123
        return log.timestampString.replace('T', ' ').replace(/(\.\d{1,3})\d*(Z|[+-].*)$/, '$1').replace('Z', '');
      }
      const d = log.timestamp as Date;
      return d.toISOString().replace('T', ' ').replace(/(\.\d{3})\d*Z$/, '$1');
    };

    const levelLabel = this.logLevelOptions.find(o => o.value === level)?.label ?? level;
    const divider = '='.repeat(80);

    const lines: string[] = [
      divider,
      'proAV Device Log Export',
      divider,
      `Device:      ${this.deviceName || this.deviceId}`,
      `Date From:   ${fmtDate(from)}`,
      `Date To:     ${fmtDate(to)}`,
      `Level:       ${levelLabel}`,
      `Exported:    ${fmtDate(new Date())}`,
      `Entries:     ${logs.length.toLocaleString()}`,
      divider,
      '',
    ];

    for (const log of logs) {
      const ts = fmtTs(log);
      const lvl = pad((log.level ?? 'info').toUpperCase(), 11);
      const cat = log.category ? `[${log.category}]` : '';
      const src = log.fileName ? `[${log.fileName}:${log.lineNumber ?? '?'}]` : '';
      const suffix = [cat, src].filter(Boolean).join('  ');
      lines.push(`${ts}  ${lvl}  ${log.message ?? ''}${suffix ? `  ${suffix}` : ''}`);
      if (log.stack) {
        const indent = ' '.repeat(ts.length + 2 + 11 + 2);
        for (const stackLine of log.stack.split('\n')) {
          lines.push(`${indent}${stackLine}`);
        }
      }
    }

    lines.push('');
    lines.push(divider);
    lines.push(`End of log export — ${logs.length.toLocaleString()} entries`);
    lines.push(divider);

    return lines.join('\n');
  }

  private triggerDownload(content: string, filename: string): void {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  ngAfterViewInit(): void {
    // If logs arrived before container, setter will handle; nothing else needed here now.
    if (this.logsContainer && this.logs().length && !this.initialScrollDone) this.scheduleInitialScroll();
  }

  ngOnDestroy(): void {
    if (this.logSub) this.logSub.unsubscribe();
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = undefined;
    }
    this.mutationObserver?.disconnect();
  }

  onScroll(): void {
    if (!this.logsContainer) return;
    const el = this.logsContainer.nativeElement;
    const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
    // If user scrolls up more than ~50px from bottom, disable auto-scroll
    const shouldAuto = distanceFromBottom < 50;
    if (this.autoScroll() !== shouldAuto) this.autoScroll.set(shouldAuto);

    // Load older when near top
    if (el.scrollTop < 40) {
      this.loadOlder();
    }

    // Load newer when near bottom in historical view
    if (this.isHistoricalView() && distanceFromBottom < 40) {
      this.loadNewer();
    }
  }

  async fetchLogTimeRange(): Promise<void> {
    if (!this.deviceId) return;
    try {
      const range = await this.api.getDeviceLogsTimeRange(this.deviceId);
      this.logTimeRange.set({
        earliest: range.earliest ? new Date(range.earliest) : null,
        latest: range.latest ? new Date(range.latest) : null
      });
    } catch (err: any) {
      this.messageService.add({ severity: 'error', summary: 'Failed to Fetch Time Range', detail: err?.error || err?.message || 'An error occurred while fetching log time range' });
    }
  }

  toggleDatePicker(): void {
    if (!this.showDatePicker()) {
      // Fetch time range when opening picker
      this.fetchLogTimeRange();
    }
    this.showDatePicker.update(v => !v);
  }

  async jumpToTimestamp(date: Date | null): Promise<void> {
    if (!date || !this.deviceId) return;

    console.log('Jumping to timestamp:', date);
    this.isHistoricalView.set(true);
    this.showDatePicker.set(false);

    // Unsubscribe from live updates
    if (this.logSub) {
      this.logSub.unsubscribe();
      this.logSub = undefined;
    }

    try {
      const level = this.selectedLogLevel();
      const search = this.searchQuery();
      // Fetch logs around the selected timestamp (50 before, 50 after)
      const [beforeResp, afterResp] = await Promise.all([
        this.api.getDeviceLogs(this.deviceId, 50, undefined, date, 'before', level, search),
        this.api.getDeviceLogs(this.deviceId, 50, undefined, date, 'after', level, search)
      ]);

      const beforeLogs = Array.isArray(beforeResp.items) ? beforeResp.items : [];
      const afterLogs = Array.isArray(afterResp.items) ? afterResp.items : [];

      // Combine and normalize
      const allRaw = [...beforeLogs, ...afterLogs];
      const normalized = this.normalizeLogs(allRaw);

      // Reset state
      this.idSet.clear();
      normalized.forEach(l => { const key = this.getLogKey(l); if (key) this.idSet.add(key); });
      this.logs.set(normalized);
      this.nextCursor = beforeResp.nextCursor || null;

      // Scroll to middle
      requestAnimationFrame(() => {
        if (this.logsContainer) {
          const el = this.logsContainer.nativeElement;
          el.scrollTop = el.scrollHeight / 2;
        }
      });
    } catch (err: any) {
      this.messageService.add({ severity: 'error', summary: 'Failed to Jump to Timestamp', detail: err?.error || err?.message || 'An error occurred while jumping to timestamp' });
    }
  }

  jumpToLatest(): void {
    console.log('Jumping to latest logs');
    this.isHistoricalView.set(false);
    this.selectedDateTime.set(null);

    // Re-fetch latest logs and re-subscribe
    if (!this.deviceId) return;

    const level = this.selectedLogLevel();
    const search = this.searchQuery();
    this.api.getDeviceLogs(this.deviceId, 200, undefined, undefined, undefined, level, search)
      .then((resp) => {
        const initialRaw = Array.isArray(resp) ? resp : (resp && Array.isArray(resp.items) ? resp.items : []);
        this.nextCursor = (resp as any)?.nextCursor || null;
        const normalized = this.normalizeLogs(initialRaw);

        // Reset state
        this.idSet.clear();
        normalized.forEach(l => { const key = this.getLogKey(l); if (key) this.idSet.add(key); });
        this.logs.set(normalized);

        // Re-subscribe to live updates
        this.logSub = this.api.subscribeToDeviceLogs(this.deviceId!).subscribe(allLogs => {
          const rawArray = Array.isArray(allLogs) ? allLogs : [];
          const liveNorm = this.normalizeLogs(rawArray);
          // Filter for new logs that match search pattern
          const newOnes = liveNorm.filter(l => {
            const key = this.getLogKey(l);
            return key && !this.idSet.has(key) && this.matchesSearchPattern(l);
          });
          if (newOnes.length === 0) return;
          newOnes.forEach(l => { const key = this.getLogKey(l); if (key) this.idSet.add(key); });
          const highlighted = newOnes.map(l => ({ ...l, _new: true }));
          this.logs.update(arr => [...arr, ...highlighted]);

          if (this.autoScroll()) {
            requestAnimationFrame(() => this.scrollToBottom());
          }

          setTimeout(() => {
            this.logs.update(arr => arr.map(item => item._new ? { ...item, _new: false } : item));
          }, 1800);
        });

        // Scroll to bottom
        this.autoScroll.set(true);
        requestAnimationFrame(() => this.scrollToBottom());
      })
      .catch(err => {
        this.messageService.add({ severity: 'error', summary: 'Failed to Jump to Latest', detail: err?.error || err?.message || 'An error occurred while jumping to latest logs' });
      });
  }

  async refetchLogsWithFilter(): Promise<void> {
    if (!this.deviceId) return;

    const level = this.selectedLogLevel();
    const search = this.searchQuery();

    console.log('Refetching logs with filters - level:', level, 'search:', search);

    try {
      // Fetch logs with the selected level and search filters
      const resp = await this.api.getDeviceLogs(this.deviceId, 200, undefined, undefined, undefined, level, search);
      const initialRaw = Array.isArray(resp) ? resp : (resp && Array.isArray(resp.items) ? resp.items : []);
      this.nextCursor = (resp as any)?.nextCursor || null;
      const normalized = this.normalizeLogs(initialRaw);

      // Reset state
      this.idSet.clear();
      normalized.forEach(l => { const key = this.getLogKey(l); if (key) this.idSet.add(key); });
      this.logs.set(normalized);

      // If in live mode, re-subscribe with filter
      if (!this.isHistoricalView()) {
        // Unsubscribe existing
        if (this.logSub) {
          this.logSub.unsubscribe();
        }

        // Re-subscribe to live updates
        this.logSub = this.api.subscribeToDeviceLogs(this.deviceId).subscribe(allLogs => {
          const rawArray = Array.isArray(allLogs) ? allLogs : [];
          const liveNorm = this.normalizeLogs(rawArray);
          // Filter for new logs that match search pattern
          const newOnes = liveNorm.filter(l => {
            const key = this.getLogKey(l);
            return key && !this.idSet.has(key) && this.matchesSearchPattern(l);
          });
          if (newOnes.length === 0) return;
          newOnes.forEach(l => { const key = this.getLogKey(l); if (key) this.idSet.add(key); });
          const highlighted = newOnes.map(l => ({ ...l, _new: true }));
          this.logs.update(arr => [...arr, ...highlighted]);

          if (this.autoScroll()) {
            requestAnimationFrame(() => this.scrollToBottom());
          }

          setTimeout(() => {
            this.logs.update(arr => arr.map(item => item._new ? { ...item, _new: false } : item));
          }, 1800);
        });
      }

      // Scroll to bottom if in live mode
      if (!this.isHistoricalView() && this.autoScroll()) {
        requestAnimationFrame(() => this.scrollToBottom());
      }
    } catch (err: any) {
      this.messageService.add({ severity: 'error', summary: 'Failed to Refetch Logs', detail: err?.error || err?.message || 'An error occurred while refetching logs with filter' });
    }
  }

  private scrollToBottomIfAuto(): void { if (this.autoScroll()) this.scrollToBottom(); }

  private isAtBottom(): boolean {
    if (!this.logsContainer) return false;
    const el = this.logsContainer.nativeElement;
    const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
    return distanceFromBottom < 5; // threshold
  }

  private isNearBottom(): boolean {
    if (!this.logsContainer) return false;
    const el = this.logsContainer.nativeElement;
    const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
    return distanceFromBottom < 120;
  }

  private scrollToBottom(): void {
    if (!this.logsContainer) return;
    const el = this.logsContainer.nativeElement;
    console.log(`Scrolling to bottom of logs, height=${el.scrollHeight}, currentTop=${el.scrollTop}, clientHeight=${el.clientHeight}, distanceFromBottom=${el.scrollHeight - (el.scrollTop + el.clientHeight)}`);
    el.scrollTop = el.scrollHeight;
  }

  private scheduleInitialScroll(): void {
    if (this.initialScrollDone || !this.logsContainer) return;
    const el = this.logsContainer.nativeElement;
    // Initial RAF to flush layout
    requestAnimationFrame(() => {
      this.scrollToBottom();
      // If not at bottom yet (content still expanding), observe until stable
      if (!this.isAtBottom()) {
        this.observeForStabilization(el);
      } else {
        this.initialScrollDone = true;
      }
    });
  }

  private observeForStabilization(el: HTMLElement): void {
    if (this.initialScrollDone) return;
    this.mutationObserver?.disconnect();
    this.mutationObserver = new MutationObserver(() => {
      this.scrollToBottom();
      if (this.isAtBottom()) {
        this.initialScrollDone = true;
        this.mutationObserver?.disconnect();
      }
    });
    this.mutationObserver.observe(el, { childList: true, subtree: true });
    // Safety stop after 1s
    setTimeout(() => {
      if (!this.initialScrollDone) {
        this.scrollToBottom();
        this.initialScrollDone = true; // Give up but leave at bottom
      }
      this.mutationObserver?.disconnect();
    }, 1000);
  }

  scrollToNow(): void {
    this.autoScroll.set(true);
    this.scrollToBottom();
  }

  private loadOlder(): void {
    if (this.loadingOlder || !this.nextCursor || !this.deviceId || !this.logsContainer) return;
    this.loadingOlder = true;
    const el = this.logsContainer.nativeElement;
    const prevScrollHeight = el.scrollHeight;
    const prevScrollTop = el.scrollTop;
    const level = this.selectedLogLevel();
    const search = this.searchQuery();
    this.api.getDeviceLogs(this.deviceId, 100, this.nextCursor, undefined, undefined, level, search)
      .then(resp => {
        const items = resp?.items || [];
        const olderNormalized = this.normalizeLogs(items);
        // Filter out duplicates before prepending
        const newOnes = olderNormalized.filter(l => {
          const key = l.timestampString || l.timestamp?.getTime().toString();
          return key && !this.idSet.has(key);
        });
        if (newOnes.length === 0) {
          this.nextCursor = resp?.nextCursor || null;
          return;
        }
        // Prepend older logs (older timestamps at start)
        newOnes.forEach(l => {
          const key = l.timestampString || l.timestamp?.getTime().toString();
          if (key) this.idSet.add(key);
        });
        this.logs.update(arr => [...newOnes, ...arr]);
        this.nextCursor = resp?.nextCursor || null;
        // Maintain viewport position after prepending
        requestAnimationFrame(() => {
          const newScrollHeight = el.scrollHeight;
          const delta = newScrollHeight - prevScrollHeight;
          el.scrollTop = prevScrollTop + delta;
        });
      })
      .catch(err => {
        this.messageService.add({ severity: 'error', summary: 'Failed to Load Older Logs', detail: err?.error || err?.message || 'An error occurred while loading older logs' });
      })
      .finally(() => { this.loadingOlder = false; });
  }

  private loadNewer(): void {
    if (this.loadingNewer || !this.deviceId || !this.logsContainer) return;
    const currentLogs = this.logs();
    if (currentLogs.length === 0) return;

    // Get the newest log's timestamp to fetch logs after it
    const newestLog = currentLogs[currentLogs.length - 1];
    if (!newestLog?.timestamp) return;

    this.loadingNewer = true;
    const level = this.selectedLogLevel();
    const search = this.searchQuery();
    this.api.getDeviceLogs(this.deviceId, 100, undefined, newestLog.timestamp, 'after', level, search)
      .then(resp => {
        const items = resp?.items || [];
        const newerNormalized = this.normalizeLogs(items);
        // Filter out duplicates
        const newOnes = newerNormalized.filter(l => { const key = this.getLogKey(l); return key && !this.idSet.has(key); });
        if (newOnes.length === 0) {
          // We've reached the latest logs, switch back to live mode
          this.jumpToLatest();
          return;
        }
        newOnes.forEach(l => { const key = this.getLogKey(l); if (key) this.idSet.add(key); });
        // Append newer logs
        this.logs.update(arr => [...arr, ...newOnes]);
      })
      .catch(err => {
        this.messageService.add({ severity: 'error', summary: 'Failed to Load Newer Logs', detail: err?.error || err?.message || 'An error occurred while loading newer logs' });
      })
      .finally(() => { this.loadingNewer = false; });
  }

  trackByLog = (index: number, log: any) => log.id || log._id || log.timestamp || index;

  private normalizeLogs(raw: any[]): any[] {
    if (!Array.isArray(raw)) return [];
    const mapped = raw.map(item => {
      const payload = item?.payload || {};
      const level = payload.level || 'info';
      const message = payload.message ?? '';
      const stack = payload.stackTrace ?? payload.stack ?? null;
      const fileName = payload.fileName ?? null;
      const lineNumber = payload.lineNumber ?? null;
      const category = payload.category ?? payload.categoryName ?? null;

      // Use high-precision timestamp string if available, otherwise fall back to timestampUtc
      const timestampStr = item.timestampUtcString || item.timestampUtc || payload.timestampUtc || item.timestamp || null;
      let ts: Date | null = null;
      if (timestampStr) {
        try { ts = new Date(timestampStr); } catch { ts = null; }
      }

      return {
        id: item.id || item._id || item.Id || undefined,
        level,
        message,
        stack,
        fileName,
        lineNumber,
        category,
        timestamp: ts ?? new Date(),
        timestampString: item.timestampUtcString || null, // Preserve original string for display
        raw: item,
        payload
      };
    });

    // Deduplicate based on timestampString (high-precision timestamp)
    const seen = new Set<string>();
    const deduplicated = mapped.filter(log => {
      // Use timestampString as the unique key, fallback to timestamp millis if not available
      const key = log.timestampString || log.timestamp?.getTime().toString() || Math.random().toString();
      if (seen.has(key)) {
        return false; // Skip duplicate
      }
      seen.add(key);
      return true;
    });

    // Sort ascending by timestamp so newest is at bottom
    // Use high-precision timestamp string for sorting when available, otherwise fall back to millisecond precision
    return deduplicated.sort((a, b) => {
      // If both have high-precision strings, compare them lexicographically (ISO format sorts correctly as string)
      if (a.timestampString && b.timestampString) {
        return a.timestampString.localeCompare(b.timestampString);
      }
      // Otherwise fall back to Date comparison (millisecond precision)
      return (a.timestamp?.getTime() || 0) - (b.timestamp?.getTime() || 0);
    });
  }

  // Breadcrumb builder removed
}
